import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Plus, Search, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import TeamForm from "@/components/teams/TeamForm";

export default function Teams() {
  const [showForm, setShowForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const queryClient = useQueryClient();

  const { data: teams = [], isLoading } = useQuery({
    queryKey: ["teams"],
    queryFn: () => base44.entities.Team.list("-created_date"),
  });

  const { data: allMembers = [] } = useQuery({
    queryKey: ["teamMembers"],
    queryFn: () => base44.entities.TeamMember.list(),
  });

  const createTeamMutation = useMutation({
    mutationFn: (data) => base44.entities.Team.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      setShowForm(false);
      setEditingTeam(null);
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Team.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      setShowForm(false);
      setEditingTeam(null);
    },
  });

  const handleSubmit = (data) => {
    if (editingTeam) {
      updateTeamMutation.mutate({ id: editingTeam.id, data });
    } else {
      createTeamMutation.mutate(data);
    }
  };

  const getMemberCount = (teamId) => {
    return allMembers.filter(m => m.team_id === teamId).length;
  };

  const filteredTeams = teams.filter(team =>
    team.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    team.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Team</h1>
            <p className="text-slate-500 mt-1">{teams.length} team totalt</p>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" />
            Opprett Team
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Søk etter team..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Teams Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <Card key={i} className="p-6 animate-pulse">
                <div className="h-6 bg-slate-200 rounded w-2/3 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-full mb-4" />
                <div className="h-4 bg-slate-200 rounded w-1/3" />
              </Card>
            ))}
          </div>
        ) : filteredTeams.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Ingen team funnet</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTeams.map((team) => (
              <Link key={team.id} to={createPageUrl("TeamDetail") + `?id=${team.id}`}>
                <Card className="p-6 bg-white border-0 shadow-sm hover:shadow-md transition-all cursor-pointer">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-3 bg-lime-100 rounded-lg">
                      <Users className="w-6 h-6 text-zinc-900" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{team.name}</h3>
                  {team.description && (
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{team.description}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Users className="w-4 h-4" />
                      <span>{getMemberCount(team.id)} medlemmer</span>
                    </div>
                    <Badge className={team.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}>
                      {team.status === "active" ? "Aktiv" : "Inaktiv"}
                    </Badge>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Team Form */}
        {showForm && (
          <TeamForm
            team={editingTeam}
            onSubmit={handleSubmit}
            onClose={() => {
              setShowForm(false);
              setEditingTeam(null);
            }}
            isLoading={createTeamMutation.isPending || updateTeamMutation.isPending}
          />
        )}
      </div>
    </div>
  );
}